import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TIssueAttachment } from "@/types/issue-attachment";
import { classifyPanelFetchError, type PanelFetchError } from "./panel-fetch-error";
import { loggableError } from "./workspace-ai-provider.store";

export class IssueAttachmentsStore {
  attachmentsByIssueId = observable.map<string, TIssueAttachment[]>();
  loadingByIssueId = observable.map<string, boolean>();
  errorByIssueId = observable.map<string, PanelFetchError | null>();
  uploadingByIssueId = observable.map<string, boolean>();
  uploadErrorByIssueId = observable.map<string, string | null>();
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getAttachments(issueId: string): TIssueAttachment[] {
    return this.attachmentsByIssueId.get(issueId) ?? [];
  }

  /** ``projectId`` is null/undefined/"" for a project-less event; the service
   *  branches on it to pick the workspace-scoped route (NG-2016). */
  async fetchAttachments(
    workspaceSlug: string,
    projectId: string | null | undefined,
    issueId: string,
  ): Promise<void> {
    this.loadingByIssueId.set(issueId, true);
    this.errorByIssueId.set(issueId, null);
    try {
      const attachments = await this.rootStore.issueAttachmentService.listAttachments(
        workspaceSlug,
        projectId,
        issueId,
      );
      runInAction(() => {
        this.attachmentsByIssueId.set(issueId, attachments);
        this.loadingByIssueId.set(issueId, false);
      });
    } catch (error) {
      const kind = classifyPanelFetchError(error);
      const log = kind === "forbidden" ? console.warn : console.error;
      log("Failed to fetch attachments", { workspaceSlug, projectId, issueId, error });
      runInAction(() => {
        this.loadingByIssueId.set(issueId, false);
        this.errorByIssueId.set(issueId, kind);
      });
    }
  }

  /** Upload one file. Never rejects: the result is whether it landed, and a
   *  failure is also recorded in `uploadErrorByIssueId`, which the full-page
   *  panel renders inline. Callers that fire and forget rely on it not
   *  throwing; the peek reads the result to toast (NG-2016). */
  async uploadAttachment(
    workspaceSlug: string,
    projectId: string | null | undefined,
    issueId: string,
    file: File,
    source: AnalyticsSource = "page",
  ): Promise<boolean> {
    this.uploadingByIssueId.set(issueId, true);
    this.uploadErrorByIssueId.set(issueId, null);
    try {
      const attachment = await this.rootStore.issueAttachmentService.upload(
        workspaceSlug,
        projectId,
        issueId,
        file,
      );
      runInAction(() => {
        const existing = this.attachmentsByIssueId.get(issueId) ?? [];
        this.attachmentsByIssueId.set(issueId, [attachment, ...existing]);
        this.uploadingByIssueId.set(issueId, false);
      });
      // Ids and the size only: the file name is customer content.
      track("task_attachment_added", {
        task_id: issueId,
        project_id: projectId ?? null,
        attachment_id: attachment.id,
        size: file.size,
        source,
      });
      return true;
    } catch (error) {
      // The panel only renders the message below, which cannot say WHY — a
      // rejected presign, a storage PUT the browser blocked and a failed
      // finalize all read the same. So log the cause with the ids before
      // swallowing it — but NEVER the raw error. When the storage upload is
      // what failed, its AxiosError carries the presigned URL (a PUT signs in
      // the query string) and the signed form fields, and console.error is
      // forwarded to Sentry. `loggableError` keeps only status/code/message;
      // its `url` is dropped too, because here it IS that signed URL, and its
      // path holds the file name. No file name either: it is customer content.
      const { url: _signedUrl, ...cause } = loggableError(error);
      console.error("Failed to upload attachment", {
        workspaceSlug,
        projectId,
        issueId,
        ...cause,
      });
      runInAction(() => {
        this.uploadingByIssueId.set(issueId, false);
        this.uploadErrorByIssueId.set(issueId, "Failed to upload attachment");
      });
      return false;
    }
  }

  async deleteAttachment(
    workspaceSlug: string,
    projectId: string | null | undefined,
    issueId: string,
    attachmentId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    try {
      await this.rootStore.issueAttachmentService.deleteAttachment(
        workspaceSlug,
        projectId,
        issueId,
        attachmentId,
      );
      runInAction(() => {
        const existing = this.attachmentsByIssueId.get(issueId) ?? [];
        this.attachmentsByIssueId.set(
          issueId,
          existing.filter((a) => a.id !== attachmentId),
        );
      });
      track("task_attachment_deleted", {
        task_id: issueId,
        project_id: projectId ?? null,
        attachment_id: attachmentId,
        source,
      });
    } catch (err) {
      // list stays intact on failure
      console.error(
        "[issue-attachments] delete attachment failed",
        { workspaceSlug, projectId, issueId, attachmentId },
        err,
      );
    }
  }
}
